import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import {
  getFirstAvailableModuleUrl,
  getRouteSegmentFromUrl,
  hasModuleRouteAccess,
  isModuleRoutePath,
  normalizeModuleIds,
} from "../_utils/module-navigation.util";

interface StoredUser {
  admin?: boolean;
  isAdmin?: boolean;
  plantAdmin?: boolean;
  isPlantAdmin?: boolean;
  moduleAccess?: { m?: number | string }[];
}

@Injectable({ providedIn: "root" })
export class ModuleRouteAccessGuard implements CanActivate, CanActivateChild {
  private readonly tenantModuleAccessStorageKey = "tenantModuleAccess";

  constructor(private readonly router: Router) {}

  canActivate(): UrlTree {
    return this.getFallbackUrlTree();
  }

  canActivateChild(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    const routePath = getRouteSegmentFromUrl(state.url);
    if (!isModuleRoutePath(routePath)) {
      return true;
    }

    const user = this.getStoredUser();

    // No user in localStorage (logged out / another tab) — redirect to login.
    // Safe: this guard doesn't run for auth routes, so no redirect loop.
    if (!user) {
      return this.router.parseUrl('/web/login?returnUrl=' + encodeURIComponent(state.url));
    }

    // Triage suite routes are always accessible to authenticated users
    if (['alert-triage', 'asset-explorer', 'knowledge-graph', 'library'].includes(routePath)) {
      return true;
    }

    if (user?.admin || user?.isAdmin || user?.plantAdmin || user?.isPlantAdmin) {
      return true;
    }

    const tenantModuleAccess = this.getStoredTenantModuleAccess();
    const hasAccess = hasModuleRouteAccess(
      routePath,
      user?.moduleAccess || [],
      tenantModuleAccess
    );
    if (hasAccess) {
      return true;
    }

    return this.getFallbackUrlTree(user?.moduleAccess || [], tenantModuleAccess);
  }

  private getStoredTenantModuleAccess(): number[] {
    try {
      const storedTenantAccess = localStorage.getItem(this.tenantModuleAccessStorageKey);
      const parsedValue = storedTenantAccess ? JSON.parse(storedTenantAccess) : [];
      return normalizeModuleIds(Array.isArray(parsedValue) ? parsedValue : []);
    } catch (error) {
      return [];
    }
  }

  private getStoredUser(): StoredUser | null {
    try {
      const storedUser = localStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      return null;
    }
  }

  private getFallbackUrlTree(
    moduleAccess = this.getStoredUser()?.moduleAccess || [],
    tenantModuleAccess = this.getStoredTenantModuleAccess()
  ): UrlTree {
    const fallbackUrl = getFirstAvailableModuleUrl(moduleAccess, tenantModuleAccess);
    return this.router.parseUrl(fallbackUrl);
  }
}
